import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { reportsApi } from '@/lib/api';
import { REGISTER } from '@/constants/testIds';
import { Loader2 } from 'lucide-react';
import { GoogleButton, OrDivider } from '@/components/GoogleButton';

const SignUp = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error('Password must be at least 6 characters');
    if (password !== confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await register(name, email, password);
      // Attach unsaved report if present
      const raw = sessionStorage.getItem('wellth_latest_report');
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed && !parsed.user_id) {
            await reportsApi.attach({ intake: parsed.intake, report: parsed.report });
          }
        } catch { /* ignore */ }
      }
      toast.success('Welcome to Wellth');
      nav(raw ? '/results' : '/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create account');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <h1 className="font-serif-display text-4xl text-[#1A2421] font-medium text-center leading-tight">Save your Wellth history</h1>
        <p className="text-center text-[#4A5A53] mt-3 font-light">A place to return, refine, and repeat.</p>

        <form onSubmit={submit} className="mt-10 bg-white border border-[#E5E1D8] rounded-2xl p-8 space-y-5">
          <GoogleButton label="Sign up with Google" testId="signup-google-button" />
          <OrDivider />
          <div>
            <Label htmlFor="name" className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-2 h-12 bg-white border-[#E5E1D8] rounded-xl" data-testid={REGISTER.nameInput} />
          </div>
          <div>
            <Label htmlFor="email" className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-2 h-12 bg-white border-[#E5E1D8] rounded-xl" data-testid={REGISTER.emailInput} />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-2 h-12 bg-white border-[#E5E1D8] rounded-xl" data-testid={REGISTER.passwordInput} />
          </div>
          <div>
            <Label htmlFor="confirm" className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4A5A53]">Confirm password</Label>
            <Input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-2 h-12 bg-white border-[#E5E1D8] rounded-xl" data-testid={REGISTER.passwordConfirmInput} />
          </div>
          <Button disabled={loading} type="submit" className="w-full rounded-full bg-[#4A7C59] hover:bg-[#3E6B4F] text-white h-12" data-testid={REGISTER.submitButton}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create account'}
          </Button>
          <p className="text-center text-sm text-[#4A5A53]">
            Already have an account?{' '}
            <Link to="/login" className="text-[#4A7C59] font-semibold hover:underline" data-testid={REGISTER.loginLink}>Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default SignUp;
